import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Building2, Mail, Phone, Globe, Wallet,
  Camera, Sparkles, Save, Plus, Minus, Clock, FileText,
  Eye, Power, PowerOff, Shield, Users, Key, X, Crown,
  AlertTriangle, Check, TrendingUp, Package, Lock, DollarSign,
  RefreshCw, Trash2
} from 'lucide-react';
import { adminAPI } from '../../api';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { formatCredits, formatBalance } from '../../utils/credits';

const AdminPartnerDetail = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creditAdjustment, setCreditAdjustment] = useState(0);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [creditType, setCreditType] = useState('topup'); // 'included' or 'topup'
  const [extendTrialDays, setExtendTrialDays] = useState(7);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [skuOverride, setSkuOverride] = useState('');
  const [showImpersonate, setShowImpersonate] = useState(false);
  const [impersonateData, setImpersonateData] = useState(null);
  const [loadingImpersonate, setLoadingImpersonate] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Pricing state
  const [pricing, setPricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);
  const [pricingValues, setPricingValues] = useState({
    tryon_cost: '',
    moments_cost: '',
    size_recommendation_cost: '',
    max_skus: '',
    notes: ''
  });

  const fetchPartner = useCallback(async () => {
    try {
      const response = await adminAPI.getPartner(partnerId);
      setData(response.data);
      setSelectedPlan(response.data.subscription_plan || 'starter');
      // Set SKU override - empty string means use plan default
      setSkuOverride(response.data.max_skus_override !== null && response.data.max_skus_override !== undefined 
        ? String(response.data.max_skus_override) 
        : '');
    } catch (error) {
      toast.error('Failed to load partner details');
      navigate('/admin/partners');
    } finally {
      setLoading(false);
    }
  }, [partnerId, navigate]);

  const fetchPartnerPricing = useCallback(async () => {
    try {
      setLoadingPricing(true);
      const response = await adminAPI.getPartnerPricing(partnerId);
      setPricing(response.data);
      setPricingValues({
        tryon_cost: response.data.overrides?.tryon_cost ?? '',
        moments_cost: response.data.overrides?.moments_cost ?? '',
        size_recommendation_cost: response.data.overrides?.size_recommendation_cost ?? '',
        max_skus: response.data.overrides?.max_skus ?? '',
        notes: response.data.overrides?.notes ?? ''
      });
    } catch (error) {
      console.error('Failed to load pricing:', error);
    } finally {
      setLoadingPricing(false);
    }
  }, [partnerId]);

  useEffect(() => {
    fetchPartner();
  }, [fetchPartner]);

  useEffect(() => {
    if (partnerId) {
      fetchPartnerPricing();
    }
  }, [partnerId, fetchPartnerPricing]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        subscription_plan: selectedPlan
      };
      
      // Handle SKU override
      // Empty string = remove override (set to 0 which backend interprets as "use plan default")
      // "-1" = unlimited
      // Number = custom limit
      if (skuOverride !== '' && skuOverride !== (data?.max_skus_override?.toString() || '')) {
        updateData.max_skus_override = parseInt(skuOverride, 10);
      } else if (skuOverride === '' && data?.max_skus_override !== null && data?.max_skus_override !== undefined) {
        // Clear the override
        updateData.max_skus_override = 0;
      }
      
      await adminAPI.updatePartner(partnerId, updateData);
      toast.success('Partner updated successfully');
      fetchPartner();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update partner');
    } finally {
      setSaving(false);
    }
  };

  const handleWalletAdjust = async () => {
    if (creditAdjustment === 0) {
      toast.error('Please enter a credit amount');
      return;
    }
    if (!adjustmentReason.trim()) {
      toast.error('Please provide a reason for adjustment');
      return;
    }

    setSaving(true);
    try {
      const response = await adminAPI.adjustWallet(partnerId, {
        credits: creditAdjustment,
        reason: adjustmentReason,
        credit_type: creditType
      });
      toast.success(`Usage balance adjusted: ${creditAdjustment > 0 ? '+' : ''}₹${creditAdjustment} (${creditType})`);
      setCreditAdjustment(0);
      setAdjustmentReason('');
      setShowWalletModal(false);
      fetchPartner();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust usage balance');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    setTogglingStatus(true);
    try {
      const response = await adminAPI.togglePartnerStatus(partnerId);
      toast.success(response.data.message);
      fetchPartner();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle status');
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleImpersonate = async () => {
    setLoadingImpersonate(true);
    try {
      const response = await adminAPI.impersonatePartner(partnerId);
      setImpersonateData(response.data);
      setShowImpersonate(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load partner view');
    } finally {
      setLoadingImpersonate(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setResettingPassword(true);
    try {
      const response = await adminAPI.resetPartnerPassword(partnerId, { new_password: newPassword });
      toast.success(response.data.message || 'Password reset successfully');
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleSavePricing = async () => {
    try {
      setSavingPricing(true);
      const updates = {};
      if (pricingValues.tryon_cost !== '' && pricingValues.tryon_cost !== null) {
        updates.tryon_cost = parseFloat(pricingValues.tryon_cost);
      }
      if (pricingValues.moments_cost !== '' && pricingValues.moments_cost !== null) {
        updates.moments_cost = parseFloat(pricingValues.moments_cost);
      }
      if (pricingValues.size_recommendation_cost !== '' && pricingValues.size_recommendation_cost !== null) {
        updates.size_recommendation_cost = parseFloat(pricingValues.size_recommendation_cost);
      }
      if (pricingValues.max_skus !== '' && pricingValues.max_skus !== null) {
        updates.max_skus = parseInt(pricingValues.max_skus);
      }
      if (pricingValues.notes) {
        updates.notes = pricingValues.notes;
      }
      
      await adminAPI.updatePartnerPricing(partnerId, updates);
      toast.success('Pricing overrides updated');
      await fetchPartnerPricing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const handleClearPricingOverrides = async () => {
    if (!window.confirm('Remove all pricing overrides and use plan defaults?')) return;
    try {
      setSavingPricing(true);
      await adminAPI.updatePartnerPricing(partnerId, { clear_overrides: true });
      toast.success('Overrides cleared');
      await fetchPartnerPricing();
    } catch (error) {
      toast.error('Failed to clear overrides');
    } finally {
      setSavingPricing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Data is returned as flat structure from API
  const partner = data;
  const wallet = data ? {
    balance: data.wallet_balance,
    included_balance: data.included_balance,
    topup_balance: data.topup_balance,
    total_credits_used: data.total_credits_used,
    total_credits_purchased: data.total_credits_purchased,
    trial_expires_at: data.trial_expires_at
  } : {};
  const recent_executions = data?.recent_executions || [];
  const audit_logs = data?.audit_logs || [];
  const isActive = partner?.is_active !== false && partner?.status !== 'suspended';

  return (
    <div className="space-y-6" data-testid="admin-partner-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/partners')}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-slate-900">{partner?.business_name}</h1>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {isActive ? 'Active' : 'Disabled'}
              </span>
            </div>
            <p className="text-slate-500">{partner?.email}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPasswordModal(true)}
            className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
            data-testid="reset-password-btn"
          >
            <Lock className="w-4 h-4" />
            Reset Password
          </button>
          <button
            onClick={handleImpersonate}
            disabled={loadingImpersonate}
            className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
            data-testid="impersonate-btn"
          >
            {loadingImpersonate ? (
              <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            View as Partner
          </button>
          <button
            onClick={handleToggleStatus}
            disabled={togglingStatus}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              isActive 
                ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
            data-testid="toggle-status-btn"
          >
            {togglingStatus ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : isActive ? (
              <><PowerOff className="w-4 h-4" /> Disable</>
            ) : (
              <><Power className="w-4 h-4" /> Enable</>
            )}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Partner Info */}
        <div className="lg:col-span-2 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-slate-200 p-6"
          >
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Partner Information</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Building2 className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-400">Business Name</p>
                  <p className="text-slate-900">{partner?.business_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-400">Email</p>
                  <p className="text-slate-900">{partner?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Phone className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-400">Phone</p>
                  <p className="text-slate-900">{partner?.phone || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Globe className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-400">Website</p>
                  <p className="text-slate-900">{partner?.website || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Clock className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-400">Registered</p>
                  <p className="text-slate-900">
                    {partner?.created_at ? format(parseISO(partner.created_at), 'MMM d, yyyy') : '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Crown className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-xs text-slate-400">Subscription</p>
                  <p className="text-slate-900 capitalize">{partner?.subscription_plan || 'Basic'}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Recent Executions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl border border-slate-200 p-6"
          >
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Recent API Executions</h3>
            {recent_executions?.length > 0 ? (
              <div className="space-y-2">
                {recent_executions.map((exec, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {exec.api_type === 'tryon' ? (
                        <Camera className="w-5 h-5 text-blue-500" />
                      ) : (
                        <Sparkles className="w-5 h-5 text-purple-500" />
                      )}
                      <div>
                        <p className="font-medium text-slate-900 capitalize">{exec.api_type}</p>
                        <p className="text-xs text-slate-400">
                          {exec.created_at ? format(parseISO(exec.created_at), 'MMM d, HH:mm') : '-'}
                        </p>
                      </div>
                    </div>
                    <span className={`font-medium ${exec.cached ? 'text-green-600' : 'text-slate-600'}`}>
                      {exec.credits_consumed} ₹ {exec.cached && '(cached  -  free)'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-4">No recent executions</p>
            )}
          </motion.div>

          {/* Audit Logs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border border-slate-200 p-6"
          >
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Audit Logs</h3>
            {audit_logs?.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {audit_logs.map((log, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <div className="flex-1">
                      <p className="text-sm text-slate-900">{log.action}</p>
                      <p className="text-xs text-slate-400">
                        {log.timestamp ? format(parseISO(log.timestamp), 'MMM d, HH:mm') : '-'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-4">No audit logs</p>
            )}
          </motion.div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Usage Balance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-slate-200 p-6"
          >
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-green-500" />
              Usage Balance
            </h3>
            <div className="text-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl mb-4">
              <p className="text-4xl font-bold text-slate-900">₹{wallet?.balance?.toLocaleString() || 0}</p>
              <p className="text-slate-500 text-sm">Total Balance</p>
            </div>
            
            <div className="grid grid-cols-2 gap-2 mb-3 text-center">
              <div className="p-2 bg-emerald-50 rounded-lg border border-emerald-100">
                <p className="text-sm font-semibold text-emerald-700">₹{(wallet?.included_balance ?? 0)?.toLocaleString()}</p>
                <p className="text-xs text-slate-500">Included</p>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-sm font-semibold text-blue-700">₹{(wallet?.topup_balance ?? 0)?.toLocaleString()}</p>
                <p className="text-xs text-slate-500">Top-up</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4 text-center">
              <div className="p-2 bg-slate-50 rounded-lg">
                <p className="text-sm font-semibold text-slate-900">₹{wallet?.total_credits_purchased?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-500">Purchased</p>
              </div>
              <div className="p-2 bg-slate-50 rounded-lg">
                <p className="text-sm font-semibold text-slate-900">₹{wallet?.total_credits_used?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-500">Used</p>
              </div>
            </div>

            {wallet?.trial_expires_at && (
              <div className="p-2 bg-amber-50 rounded-lg mb-3 text-center border border-amber-100">
                <p className="text-xs text-amber-700">Trial expires: {new Date(wallet.trial_expires_at).toLocaleDateString()}</p>
              </div>
            )}

            <button
              onClick={() => setShowWalletModal(true)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              data-testid="adjust-wallet-btn"
            >
              <Plus className="w-4 h-4" /> Adjust Usage Balance
            </button>
          </motion.div>

          {/* Subscription Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl border border-slate-200 p-6"
          >
            <h3 className="font-semibold text-slate-900 mb-4">Subscription Plan</h3>
            <div className="space-y-2">
              {['trial', 'starter', 'growth', 'professional', 'enterprise'].map((plan) => (
                <button
                  key={plan}
                  onClick={() => setSelectedPlan(plan)}
                  className={`w-full p-3 rounded-xl border-2 transition-all capitalize ${
                    selectedPlan === plan
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {plan}
                </button>
              ))}
            </div>

            {/* Trial extension for trial plan */}
            {(selectedPlan === 'trial' || partner?.subscription_plan === 'trial') && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-2">Extend Trial Period</p>
                {wallet?.trial_expires_at && (
                  <p className="text-xs text-slate-500 mb-2">
                    Current expiry: {new Date(wallet.trial_expires_at).toLocaleDateString()}
                  </p>
                )}
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={extendTrialDays}
                    onChange={(e) => setExtendTrialDays(Math.max(1, parseInt(e.target.value) || 1))}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    min="1"
                    max="365"
                    placeholder="Days"
                  />
                  <button
                    onClick={async () => {
                      try {
                        const API_URL = process.env.REACT_APP_BACKEND_URL;
                        const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
                        await fetch(`${API_URL}/api/admin/subscriptions/extend-trial`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                          body: JSON.stringify({ partner_id: partnerId, extend_days: extendTrialDays })
                        }).then(r => r.json());
                        toast.success(`Trial extended by ${extendTrialDays} days`);
                        fetchPartner();
                      } catch (e) {
                        toast.error('Failed to extend trial');
                      }
                    }}
                    className="px-3 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
                  >
                    Extend
                  </button>
                </div>
              </div>
            )}
          </motion.div>

          {/* SKU Limit Override */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-xl border border-slate-200 p-6"
          >
            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-500" />
              SKU Limit Override
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Override the plan&apos;s default SKU limit for this partner.
            </p>
            
            {/* Current Info */}
            <div className="p-3 bg-slate-50 rounded-lg mb-4 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Plan Default:</span>
                <span className="font-medium text-slate-700">
                  {data?.plan_default_skus === -1 ? 'Unlimited' : data?.plan_default_skus || 0} SKUs
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Current Override:</span>
                <span className="font-medium text-slate-700">
                  {data?.max_skus_override === -1 ? 'Unlimited' : 
                   data?.max_skus_override !== null && data?.max_skus_override !== undefined ? `${data.max_skus_override} SKUs` : 'None'}
                </span>
              </div>
              <div className="flex justify-between text-sm pt-1 border-t border-slate-200">
                <span className="text-slate-600 font-medium">Effective Limit:</span>
                <span className="font-semibold text-blue-600">
                  {data?.effective_max_skus === -1 ? 'Unlimited' : data?.effective_max_skus || 0} SKUs
                </span>
              </div>
            </div>

            {/* Input */}
            <div className="space-y-2">
              <label className="text-sm text-slate-600">New Override Value</label>
              <input
                type="number"
                value={skuOverride}
                onChange={(e) => setSkuOverride(e.target.value)}
                placeholder="Leave empty for plan default"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="sku-override-input"
              />
              <p className="text-xs text-slate-400">
                Enter -1 for unlimited, or a specific number. Leave empty to use plan default.
              </p>
            </div>
          </motion.div>

          {/* Pricing Override Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border border-slate-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-500" />
                Pricing Override
              </h3>
              {pricing?.has_overrides && (
                <button
                  onClick={handleClearPricingOverrides}
                  disabled={savingPricing}
                  className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" />
                  Clear
                </button>
              )}
            </div>

            {loadingPricing ? (
              <div className="flex justify-center py-4">
                <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : pricing ? (
              <div className="space-y-4">
                {/* Current Pricing Display */}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Plan TryOn</p>
                    <p className="font-medium">₹{pricing.plan_defaults?.tryon_cost}/call</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Plan Moments</p>
                    <p className="font-medium">₹{pricing.plan_defaults?.moments_cost}/call</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded-lg col-span-2">
                    <p className="text-xs text-slate-500">Plan Size Rec.</p>
                    <p className="font-medium">₹{pricing.plan_defaults?.size_recommendation_cost}/call</p>
                  </div>
                  <div className="p-2 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-emerald-600">Effective TryOn</p>
                    <p className="font-semibold text-emerald-700">{formatCredits(pricing.effective?.tryon_cost)} cr</p>
                  </div>
                  <div className="p-2 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-emerald-600">Effective Moments</p>
                    <p className="font-semibold text-emerald-700">{formatCredits(pricing.effective?.moments_cost)} cr</p>
                  </div>
                  <div className="p-2 bg-emerald-50 rounded-lg col-span-2">
                    <p className="text-xs text-emerald-600">Effective Size Rec.</p>
                    <p className="font-semibold text-emerald-700">{formatCredits(pricing.effective?.size_recommendation_cost)} cr</p>
                  </div>
                </div>

                {/* Override Inputs */}
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  <div>
                    <label className="text-xs text-slate-500">TryOn Cost Override</label>
                    <input
                      type="number"
                      step="0.01"
                      value={pricingValues.tryon_cost}
                      onChange={(e) => setPricingValues(prev => ({ ...prev, tryon_cost: e.target.value }))}
                      placeholder={pricing.plan_defaults?.tryon_cost}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      data-testid="tryon-cost-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Moments Cost Override</label>
                    <input
                      type="number"
                      step="0.01"
                      value={pricingValues.moments_cost}
                      onChange={(e) => setPricingValues(prev => ({ ...prev, moments_cost: e.target.value }))}
                      placeholder={pricing.plan_defaults?.moments_cost}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      data-testid="moments-cost-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Size Recommendation Cost Override</label>
                    <input
                      type="number"
                      step="0.01"
                      value={pricingValues.size_recommendation_cost}
                      onChange={(e) => setPricingValues(prev => ({ ...prev, size_recommendation_cost: e.target.value }))}
                      placeholder={pricing.plan_defaults?.size_recommendation_cost}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      data-testid="size-recommendation-cost-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Notes</label>
                    <input
                      type="text"
                      value={pricingValues.notes}
                      onChange={(e) => setPricingValues(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="e.g., Special rate for XYZ"
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <button
                    onClick={handleSavePricing}
                    disabled={savingPricing}
                    className="w-full px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                    data-testid="save-pricing-btn"
                  >
                    {savingPricing ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <><Save className="w-4 h-4" /> Save Pricing</>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">Unable to load pricing</p>
            )}
          </motion.div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full px-4 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <><Save className="w-5 h-5" /> Save Changes</>
            )}
          </button>
        </div>
      </div>

      {/* Wallet Adjustment Modal */}
      <AnimatePresence>
        {showWalletModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl max-w-md w-full p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-900">Adjust Usage Balance</h3>
                <button onClick={() => setShowWalletModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Balance type selector */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Balance Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setCreditType('included')}
                      className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                        creditType === 'included' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600'
                      }`}
                    >
                      Included Balance
                    </button>
                    <button
                      onClick={() => setCreditType('topup')}
                      className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                        creditType === 'topup' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'
                      }`}
                    >
                      Added Usage (Top-up)
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {creditType === 'included' ? 'Adjusts the plan-included balance bucket' : 'Adjusts the top-up/purchased balance bucket'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Amount (₹)</label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCreditAdjustment(prev => prev - 100)}
                      className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      value={creditAdjustment}
                      onChange={(e) => setCreditAdjustment(parseInt(e.target.value) || 0)}
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-center text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      data-testid="credit-amount-input"
                    />
                    <button
                      onClick={() => setCreditAdjustment(prev => prev + 100)}
                      className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                <p className={`text-sm mt-1 ${creditAdjustment > 0 ? 'text-green-600' : creditAdjustment < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                {creditAdjustment > 0 ? `+₹${creditAdjustment} usage balance will be added` : 
                 creditAdjustment < 0 ? `₹${Math.abs(creditAdjustment)} usage balance will be deducted` : 
                 'Enter amount to adjust'}
              </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Reason *</label>
                  <input
                    type="text"
                    value={adjustmentReason}
                    onChange={(e) => setAdjustmentReason(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    placeholder="e.g., Compensation for outage"
                    data-testid="adjustment-reason-input"
                  />
                </div>

              {creditAdjustment < 0 && wallet?.balance + creditAdjustment < 0 && (
                  <div className="p-3 bg-red-50 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                    <p className="text-sm text-red-700">Cannot deduct more than current balance ({wallet?.balance || 0})</p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowWalletModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleWalletAdjust}
                    disabled={saving || creditAdjustment === 0 || !adjustmentReason.trim()}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-adjust-btn"
                  >
                    {saving ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <><Check className="w-4 h-4" /> Confirm</>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Password Reset Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl max-w-md w-full p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-amber-500" />
                  Reset Password
                </h3>
                <button onClick={() => { setShowPasswordModal(false); setNewPassword(''); setConfirmPassword(''); }} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mb-4 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                <p className="text-sm text-amber-800">
                  This will set a new password for <strong>{partner?.email}</strong>. The partner will need to use this new password to login.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    placeholder="Minimum 6 characters"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    placeholder="Re-enter password"
                    data-testid="confirm-password-input"
                  />
                </div>

                {newPassword && confirmPassword && newPassword !== confirmPassword && (
                  <div className="p-3 bg-red-50 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                    <p className="text-sm text-red-700">Passwords do not match</p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => { setShowPasswordModal(false); setNewPassword(''); setConfirmPassword(''); }}
                    className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePasswordReset}
                    disabled={resettingPassword || !newPassword || newPassword.length < 6 || newPassword !== confirmPassword}
                    className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-reset-password-btn"
                  >
                    {resettingPassword ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <><Lock className="w-4 h-4" /> Reset Password</>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Impersonate Modal */}
      <AnimatePresence>
        {showImpersonate && impersonateData && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl max-w-4xl w-full p-6 my-8"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Eye className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-slate-900">
                    Partner View: {impersonateData.partner.business_name}
                  </h3>
                </div>
                <button onClick={() => setShowImpersonate(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mb-6 flex items-center gap-2">
                <Eye className="w-4 h-4 text-amber-600" />
                <p className="text-sm text-amber-800">This is a read-only view of the partner&apos;s key metrics and settings.</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Partner Info */}
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-500" />
                      Partner Details
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Email</span>
                        <span className="text-slate-900">{impersonateData.partner.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Phone</span>
                        <span className="text-slate-900">{impersonateData.partner.phone || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Status</span>
                        <span className={impersonateData.partner.is_active ? 'text-green-600' : 'text-red-600'}>
                          {impersonateData.partner.is_active ? 'Active' : 'Disabled'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Plan</span>
                        <span className="text-slate-900 capitalize">{impersonateData.partner.subscription_plan}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-xl">
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-green-500" />
                      Wallet
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Balance</span>
                        <span className="text-slate-900 font-semibold">{impersonateData.wallet.balance.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Total Purchased</span>
                        <span className="text-slate-900">{impersonateData.wallet.total_purchased.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Total Used</span>
                        <span className="text-slate-900">{impersonateData.wallet.total_used.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Daily Limit</span>
                        <span className="text-slate-900">{impersonateData.wallet.daily_limit.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Settings & Stats */}
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-500" />
                      API Settings
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Rate Limit (Customer)</span>
                        <span className="text-slate-900">{impersonateData.settings.rate_limit_per_customer || 'Not set'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Rate Limit (SKU)</span>
                        <span className="text-slate-900">{impersonateData.settings.rate_limit_per_sku || 'Not set'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Monthly Budget</span>
                        <span className="text-slate-900">{impersonateData.settings.monthly_budget_limit || 'Not set'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Watermark</span>
                        <span className={impersonateData.settings.watermark_enabled ? 'text-green-600' : 'text-slate-400'}>
                          {impersonateData.settings.watermark_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-xl">
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-purple-500" />
                      7-Day Stats
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-white rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-600">{impersonateData.stats.tryon_7d}</p>
                        <p className="text-xs text-slate-500">TryOn Calls</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-600">{impersonateData.stats.moments_7d}</p>
                        <p className="text-xs text-slate-500">Moments Calls</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg text-center">
                        <p className="text-2xl font-bold text-slate-900">{impersonateData.stats.api_keys_count}</p>
                        <p className="text-xs text-slate-500">API Keys</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg text-center">
                        <p className="text-2xl font-bold text-slate-900">{impersonateData.stats.team_members_count}</p>
                        <p className="text-xs text-slate-500">Team Members</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* API Keys (masked) */}
              <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <Key className="w-4 h-4 text-amber-500" />
                  API Keys
                </h4>
                <div className="space-y-2">
                  {impersonateData.api_keys.map((key, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-white rounded-lg">
                      <span className="font-mono text-sm text-slate-600">{key.api_key}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${key.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {key.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowImpersonate(false)}
                  className="px-6 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPartnerDetail;
